// Tells the relay which conversation this client is looking at (push decisions E-H:
// no OS push for what you are reading, and push-clears for what you just read).
// Frame: cyborg:active_view {workspaceId, view, foreground}. The relay's copy is
// CONNECTION-scoped, so a reconnect must re-send it (syncAppStateToRelay).

import { reportUiFailure } from "../report-failure.js";
import type { PaneConversation } from "./conversation-rendered.js";

export type ActiveView =
  | { kind: "dm"; peerId: string }
  | { kind: "channel"; channelId: string }
  | { kind: "thread"; rootId: string }
  | null;

export interface ActiveViewFrame {
  workspaceId: string;
  view: ActiveView;
  foreground: boolean;
}

export const ACTIVE_VIEW_DEBOUNCE_MS = 300;
// The relay drops a socket's view after 30 s without a non-ping frame; re-send well inside that.
export const ACTIVE_VIEW_HEARTBEAT_MS = 20_000;

// The open thread pane wins over the route it sits beside; otherwise the route names it.
// An agent DM (`/agent/<id>`) is a dm view whose peerId is the id the server writes as
// dm_peer_id (the cybo id), so `agentPeer` maps the route's id (cybo or live session) to it.
export function activeViewFromRoute(
  pathname: string,
  openThreadRootId: string | null,
  agentPeer: (routeId: string) => string = (id) => id,
  alertsPane: PaneConversation | null = null,
): ActiveView {
  if (openThreadRootId) return { kind: "thread", rootId: openThreadRootId };
  // The Alerts route's own path names no conversation; what its context pane renders does.
  if (/^\/workspace\/[^/]+\/activity(?:[/?#]|$)/.test(pathname)) {
    if (!alertsPane) return null; // null: wire value, pane shows no conversation
    if (alertsPane.kind === "channel") return { kind: "channel", channelId: alertsPane.id };
    return {
      kind: "dm",
      peerId: alertsPane.kind === "agent" ? agentPeer(alertsPane.id) : alertsPane.id,
    };
  }
  const m = /^\/workspace\/[^/]+\/(channel|dm|agent)\/([^/?#]+)/.exec(pathname);
  if (!m || (m[1] === "agent" && m[2] === "new")) return null; // null: wire value, client is on no conversation
  const id = decodeURIComponent(m[2]);
  if (m[1] === "channel") return { kind: "channel", channelId: id };
  return { kind: "dm", peerId: m[1] === "agent" ? agentPeer(id) : id };
}

// Electron windows answer hasFocus() too, so one rule covers web and desktop.
export function isClientForeground(): boolean {
  return (
    typeof document !== "undefined" && document.visibilityState === "visible" && document.hasFocus()
  );
}

export interface ActiveViewSender {
  /** Record the open conversation; the frame goes out after the debounce. */
  set(workspaceId: string, view: ActiveView): void;
  /** Focus/blur/visibility changed: re-send the current view with the new foreground bit. */
  touch(): void;
  /** Send right now (a freshly authenticated socket). */
  resend(): void;
  /** Blur / hidden: the foreground bit must not wait out the debounce. */
  touchNow(): void;
  /** The page is going away: tell the relay no view is open, now, without forgetting the view. */
  leave(): void;
  /** The workspace layout is destroyed: the open view becomes null, sent now. */
  clear(): void;
}

export function createActiveViewSender(opts: {
  send: (frame: ActiveViewFrame) => void;
  foreground?: () => boolean;
  debounceMs?: number;
}): ActiveViewSender {
  const foreground = opts.foreground ?? isClientForeground;
  const debounceMs = opts.debounceMs ?? ACTIVE_VIEW_DEBOUNCE_MS;
  let current: { workspaceId: string; view: ActiveView } | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let beat: ReturnType<typeof setInterval> | null = null;

  const flush = (override?: { view: ActiveView; foreground: boolean }): void => {
    if (timer) clearTimeout(timer);
    timer = null;
    if (!current) return;
    const frame = { ...current, foreground: foreground(), ...override };
    // Heartbeat only while a view is open AND foreground; every flush re-decides.
    const wantBeat = frame.view !== null && frame.foreground;
    if (wantBeat && !beat) beat = setInterval(() => flush(), ACTIVE_VIEW_HEARTBEAT_MS);
    else if (!wantBeat && beat) {
      clearInterval(beat);
      beat = null;
    }
    try {
      opts.send(frame);
    } catch (err) {
      // Transient (socket closed mid-send): the post-auth resend() carries the latest value.
      reportUiFailure(
        "activeView.send",
        err,
        { workspaceId: current.workspaceId },
        { failureKind: "transient" },
      );
    }
  };
  const schedule = (): void => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(flush, debounceMs);
  };

  return {
    set(workspaceId, view) {
      current = { workspaceId, view };
      schedule();
    },
    touch() {
      if (current) schedule();
    },
    resend: () => flush(),
    touchNow() {
      if (current) flush();
    },
    leave() {
      flush({ view: null, foreground: false });
    },
    clear() {
      if (!current) return;
      current = { ...current, view: null };
      flush();
    },
  };
}

// Window focus/blur/visibility → sender.touch(). Returns the unbind.
export function bindActiveViewListeners(sender: ActiveViewSender): () => void {
  if (typeof window === "undefined") return () => {};
  const touch = () => sender.touch();
  const touchNow = () => sender.touchNow();
  const onVisibility = () =>
    document.visibilityState === "hidden" ? sender.touchNow() : sender.touch();
  const leave = () => sender.leave();
  window.addEventListener("focus", touch);
  window.addEventListener("blur", touchNow);
  window.addEventListener("pagehide", leave);
  window.addEventListener("beforeunload", leave);
  document.addEventListener("visibilitychange", onVisibility);
  return () => {
    window.removeEventListener("focus", touch);
    window.removeEventListener("blur", touchNow);
    window.removeEventListener("pagehide", leave);
    window.removeEventListener("beforeunload", leave);
    document.removeEventListener("visibilitychange", onVisibility);
  };
}
